import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Locations
export const getLocations = () => api.get('/locations');
export const createLocation = (data) => api.post('/locations', data);
export const updateLocation = (id, data) => api.put(`/locations/${id}`, data);
export const deleteLocation = (id) => api.delete(`/locations/${id}`);

// Categories
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// Repair Shops
export const getRepairShops = () => api.get('/repair-shops');
export const createRepairShop = (data) => api.post('/repair-shops', data);
export const updateRepairShop = (id, data) => api.put(`/repair-shops/${id}`, data);
export const deleteRepairShop = (id) => api.delete(`/repair-shops/${id}`);

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Assets
export const getAssets = (params) => api.get('/assets', { params });
export const getAssetByBarcode = (barcode) => api.get(`/assets/barcode/${barcode}`);
export const getAsset = (id) => api.get(`/assets/${id}`);
export const createAsset = (data) => api.post('/assets', data);
export const updateAsset = (id, data) => api.put(`/assets/${id}`, data);
export const deleteAsset = (id) => api.delete(`/assets/${id}`);
export const getAssetEvents = (id) => api.get(`/assets/${id}/events`);
export const getIncomingAssets = (locationId) => api.get(`/assets/incoming?location_id=${locationId}`);
export const getSentAssets = (locationId) => api.get(`/assets/sent?location_id=${locationId}`);

// Asset Workflows
export const sendToRepair = (id, data) => api.post(`/assets/${id}/send-repair`, data);
export const sendToLocation = (id, data) => api.post(`/assets/${id}/send-location`, data);
export const sendToTrash = (id, data) => api.post(`/assets/${id}/send-trash`, data);
export const receiveAsset = (id, locationId, data) => api.post(`/assets/${id}/receive?location_id=${locationId}`, data);

// Edit & Removal Requests
export const requestAssetEdit = (id, data) => api.post(`/assets/${id}/request-edit`, data);
export const requestAssetRemoval = (id, data) => api.post(`/assets/${id}/request-removal`, data);
export const removeAssetPhotos = (id, data) => api.post(`/assets/${id}/remove-photos`, data);

// Pending Requests
export const getPendingRequests = () => api.get('/pending-requests');
export const getMyPendingRequests = () => api.get('/pending-requests/mine');
export const reviewPendingRequest = (id, data) => api.put(`/pending-requests/${id}/review`, data);

// Notifications
export const getNotifications = () => api.get('/notifications');
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllRead = () => api.put('/notifications/read-all');

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });

// Reports
export const getAssetsByLocation = () => api.get('/reports/assets-by-location');
export const getAssetsByCategory = () => api.get('/reports/assets-by-category');
export const getAssetsByStatus = () => api.get('/reports/assets-by-status');

// Trashed/Removed Assets
export const getTrashedAssets = () => api.get('/assets/trashed');

// Sold Assets
export const getSoldAssets = (params) => api.get('/assets/sold', { params });

// Parent-Child Asset Management
export const getChildAssets = (parentId) => api.get(`/assets/${parentId}/children`);
export const linkChildAsset = (parentId, childAssetId, reason) => 
  api.post(`/assets/${parentId}/link-child`, { child_asset_id: childAssetId, reason });
export const unlinkAsset = (assetId, reason) => 
  api.post(`/assets/${assetId}/unlink`, { reason });
export const transferWithChildren = (parentId, data) => 
  api.post(`/assets/${parentId}/transfer-with-children`, data);

// Sell Asset
export const sellAsset = (id, data) => api.post(`/assets/${id}/sell`, data);

// Reinstate Asset
export const reinstateAsset = (id, reason) => api.post(`/assets/${id}/reinstate`, { reason });

// Asset Photos
export const getAssetPhotos = (id) => api.get(`/assets/${id}/photos`);

// Email Settings
export const getEmailSettings = () => api.get('/email-settings');
export const testEmail = (to_email) => api.post('/email-settings/test', { to_email });

// Messaging
export const getMessageRecipients = (locationId) => api.get(`/messages/recipients?location_id=${locationId}`);
export const sendMessage = (data) => api.post('/messages', data);
export const getMessages = (locationId) => api.get('/messages', { params: { location_id: locationId } });
export const getConversations = (locationId) => api.get(`/messages/conversations?location_id=${locationId}`);
export const getMessageThread = (threadId, locationId) => api.get(`/messages/thread/${threadId}?location_id=${locationId}`);
export const getUnreadMessageCount = () => api.get('/messages/unread-count');

// Admin Setup
export const runAdminSetup = () => api.post('/admin-setup');
export const checkUserRoles = (email) => api.get(`/check-user-roles/${encodeURIComponent(email)}`);

// Seed
export const seedData = () => api.post('/seed');

export default api;
